/**
 * Role Transfer modal – same behaviour as Angular StaffTransferComponent.
 * Selected contacts are transferred to the chosen company via POST /api/transfer_roles.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { createPortal } from "react-dom"
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock"
import { X, Loader2, AlertCircle } from "lucide-react"
import apiClient from "@/lib/api/apiClient"
import { formatApiError, showSuccess, showError } from "@/lib/utils/notifications"
import { SearchableSelect } from "@/components/ui/SearchableSelect"
import { cn } from "@/lib/utils/helpers"
import modalStyles from "@/features/application/styles/modals.module.css"
import companyFormStyles from "@/features/company/styles/companyAdd.module.css"
import transferStyles from "@/features/company/styles/roleTransferModal.module.css"
import { COMPANY_ROLE_NAMES } from '../constants'
import type { CompanyStaffMember, RoleTransferModalProps, RoleTransferCompanyOption, RoleTransferStaffEdits, RoleTransferStaffFieldErrors, RoleTransferUpdateUserRow, RoleTransferRowFieldErrors } from '../types'

/** Angular `mobileValidator` after digit strip: optional 1–2 digit country + 10 NANP → 10–12 digits. */
const MOBILE_PATTERN = /^\d{10,12}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function formatPhone(raw: string | undefined | null): string {
    if (!raw) return ""
    const digits = String(raw).replace(/\D/g, "")
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    if (digits.length === 11 && digits.startsWith("1")) {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
    }
    return raw
}

export function RoleTransferModal({
    isOpen,
    onClose,
    companyId,
    companyName,
    staffList: staffListProp = [],
    onSuccess,
}: RoleTransferModalProps) {
    const [transferToCompanyId, setTransferToCompanyId] = useState<string>("")
    const [companyOptions, setCompanyOptions] = useState<RoleTransferCompanyOption[]>([])
    const [companiesLoading, setCompaniesLoading] = useState(false)
    const [staffList, setStaffList] = useState<CompanyStaffMember[]>([])
    const [staffLoading, setStaffLoading] = useState(false)
    const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])
    const [submitting, setSubmitting] = useState(false)
    const [transferStaffEdits, setTransferStaffEdits] = useState<RoleTransferStaffEdits>({})
    const selectAllRef = useRef<HTMLInputElement>(null)

    const loadStaffs = useCallback(async () => {
        const id = companyId != null && String(companyId).trim() !== "" ? companyId : null
        if (id == null) return
        setStaffLoading(true)
        try {
            const res = await apiClient.get("/api/property_staff", {
                params: { company_id: id, sortBy: "desc" },
            })
            const raw = res?.data ?? res
            const list = Array.isArray(raw)
                ? raw
                : Array.isArray(raw?.data)
                  ? raw.data
                  : Array.isArray(raw?.staff)
                    ? raw.staff
                    : []
            setStaffList(list)
        } catch (err) {
            setStaffList([])
            showError(formatApiError(err))
        } finally {
            setStaffLoading(false)
        }
    }, [companyId])

    const loadCompanies = useCallback(async (search?: string) => {
        setCompaniesLoading(true)
        try {
            const params: Record<string, string> = {}
            if (search && search.trim()) params.q = search.trim()
            const res = await apiClient.get("/api/all_company", { params })
            const list: { company_id?: string | number; company_name?: string }[] =
                Array.isArray(res?.data) ? res.data : []
            const options = list
                .filter((c) => String(c?.company_id) !== String(companyId))
                .map((c) => ({
                    value: String(c.company_id),
                    label: c.company_name || "",
                }))
            setCompanyOptions(options)
        } catch {
            setCompanyOptions([])
        } finally {
            setCompaniesLoading(false)
        }
    }, [companyId])

    useEffect(() => {
        if (isOpen) {
            setTransferToCompanyId("")
            setSelectedRoleIds([])
            setTransferStaffEdits({})
            setStaffList(Array.isArray(staffListProp) ? staffListProp : [])
            loadCompanies()
            loadStaffs()
        }
    }, [isOpen, loadCompanies, loadStaffs])

    const displayStaffList = staffList.length > 0 ? staffList : (Array.isArray(staffListProp) ? staffListProp : [])

    const toggleSelectAll = (checked: boolean) => {
        if (checked) {
            const ids = displayStaffList
                .map((s) => s.role_id)
                .filter((id): id is number => typeof id === "number")
            setSelectedRoleIds(ids)
            setTransferStaffEdits((prev) => {
                const next = { ...prev }
                for (const staff of displayStaffList) {
                    if (typeof staff.role_id !== "number") continue
                    if (!next[staff.role_id]) {
                        next[staff.role_id] = {
                            mobile_no: String(staff.mobile_no ?? ""),
                            email: String(staff.email ?? ""),
                        }
                    }
                }
                return next
            })
        } else {
            setSelectedRoleIds([])
        }
    }

    const toggleRole = (roleId: number, checked: boolean) => {
        if (checked) {
            setSelectedRoleIds((prev) => (prev.includes(roleId) ? prev : [...prev, roleId]))
            const staff = displayStaffList.find((s) => s.role_id === roleId)
            if (staff) {
                setTransferStaffEdits((prev) => ({
                    ...prev,
                    [roleId]: {
                        mobile_no: String(staff.mobile_no ?? ""),
                        email: String(staff.email ?? ""),
                    },
                }))
            }
        } else {
            setSelectedRoleIds((prev) => prev.filter((id) => id !== roleId))
        }
    }



    const validationErrors = useMemo(() => {
        const errs: RoleTransferStaffFieldErrors = {}
        for (const roleId of selectedRoleIds) {
            const staff = displayStaffList.find((s) => s.role_id === roleId)
            if (!staff) continue
            const edits = transferStaffEdits[roleId] || {
                mobile_no: String(staff.mobile_no ?? ""),
                email: String(staff.email ?? ""),
            }
            const rowErr: RoleTransferRowFieldErrors = {}
            const digits = String(edits.mobile_no ?? "").replace(/\D/g, "")
            if (!digits) {
                rowErr.mobile_no = "Phone is required."
            } else if (!MOBILE_PATTERN.test(digits)) {
                rowErr.mobile_no = "Invalid phone number"
            }
            const email = String(edits.email ?? "").trim()
            if (!email) {
                rowErr.email = "Email is required."
            } else if (!EMAIL_PATTERN.test(email)) {
                rowErr.email = "Invalid email address"
            }
            if (rowErr.mobile_no || rowErr.email) {
                errs[roleId] = rowErr
            }
        }
        return errs
    }, [selectedRoleIds, transferStaffEdits, displayStaffList])

    const hasAnyValidationError = Object.keys(validationErrors).length > 0

    const handleTransfer = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!transferToCompanyId || selectedRoleIds.length === 0 || hasAnyValidationError) return
        setSubmitting(true)
        const updateUserinfo: RoleTransferUpdateUserRow[] = []

        for (const roleId of selectedRoleIds) {
            const staff = displayStaffList.find((s) => s.role_id === roleId)
            if (!staff) continue
            const edits = transferStaffEdits[roleId] || {
                mobile_no: String(staff.mobile_no ?? ""),
                email: String(staff.email ?? ""),
            }
            const digits = String(edits.mobile_no ?? "").replace(/\D/g, "")
            const email = String(edits.email ?? "").trim()
            const origDigits = String(staff.mobile_no ?? "").replace(/\D/g, "")
            const origEmail = String(staff.email ?? "").trim()
            if (digits !== origDigits || email !== origEmail) {
                updateUserinfo.push({ role_id: roleId, mobile_no: digits, email })
            }
        }

        try {
            await apiClient.post("/api/transfer_roles", {
                type: "COMPANY",
                company_id: Number(transferToCompanyId),
                role_ids: selectedRoleIds,
                update_userinfo: updateUserinfo,
            })
            showSuccess("Role Transfer Successfully!")
            onSuccess?.()
            onClose()
        } catch (err: unknown) {
            showError(formatApiError(err))
        } finally {
            setSubmitting(false)
        }
    }

    const staffWithRole = displayStaffList.filter((s) => s.role_id != null)
    const selectAllChecked = staffWithRole.length > 0 && selectedRoleIds.length === staffWithRole.length
    const selectAllIndeterminate = selectedRoleIds.length > 0 && selectedRoleIds.length < staffWithRole.length
    const canSubmit = !submitting && !!transferToCompanyId && selectedRoleIds.length > 0 && !hasAnyValidationError

    useEffect(() => {
        const el = selectAllRef.current
        if (el) el.indeterminate = selectAllIndeterminate
    }, [selectAllIndeterminate])

    useBodyScrollLock(isOpen)

    if (!isOpen) return null

    const content = (
        <div
            data-testid="company-role-transfer-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-transfer-title"
            className={`${modalStyles.modalOverlay} !z-[10001]`}
            onClick={onClose}
        >
            <div
                data-testid="company-role-transfer-panel"
                className={`${modalStyles.modalContent} ${modalStyles.modalContent800} ${modalStyles.modalContentStacked}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={transferStyles.header}>
                    <div className={transferStyles.headerLead}>
                        <div className="min-w-0">
                            <h2 id="role-transfer-title" className={transferStyles.title}>
                                Role Transfer
                            </h2>
                        </div>
                    </div>
                    <button
                        data-testid="company-role-transfer-close-button"
                        type="button"
                        onClick={onClose}
                        className={`${modalStyles.closeButton} ${modalStyles.modalValidationClose}`}
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleTransfer} className="flex min-h-0 flex-1 flex-col">
                    <div className={transferStyles.companyPanel}>
                        <div>
                            <span className={transferStyles.fieldLabel}>Current Company</span>
                            <p className={transferStyles.fieldValue}>{companyName || "—"}</p>
                        </div>
                        <div>
                            <span className={transferStyles.fieldLabel}>Transfer to Company</span>
                            <SearchableSelect
                                triggerDataTestId="company-role-transfer-company-trigger"
                                options={companyOptions}
                                value={transferToCompanyId}
                                onChange={(v) => setTransferToCompanyId(Array.isArray(v) ? v[0] ?? "" : v)}
                                placeholder="Select Company"
                                searchPlaceholder="Search by company name"
                                onSearch={(q) => loadCompanies(q)}
                                loading={companiesLoading}
                                onOpen={() => loadCompanies()}
                            />
                        </div>
                    </div>

                    <div className={transferStyles.tableRegion}>
                        {staffLoading ? (
                            <div data-testid="company-role-transfer-loading" className={transferStyles.loadingState}>
                                <Loader2 size={18} className="animate-spin text-[rgb(21_151_255)]" />
                                <span>Loading contacts…</span>
                            </div>
                        ) : displayStaffList.length === 0 ? (
                            <p data-testid="company-role-transfer-empty" className={transferStyles.emptyState}>
                                No contacts to transfer.
                            </p>
                        ) : (
                            <div data-testid="company-role-transfer-table-wrap" className={transferStyles.tableWrap}>
                                <table data-testid="company-role-transfer-table" className={transferStyles.table}>
                                    <thead className={transferStyles.tableHead}>
                                        <tr>
                                            <th style={{ width: "120px" }}>
                                                <label className={transferStyles.selectAllLabel}>
                                                    <input
                                                        data-testid="company-role-transfer-select-all-checkbox"
                                                        type="checkbox"
                                                        ref={selectAllRef}
                                                        checked={selectAllChecked}
                                                        onChange={(e) => toggleSelectAll(e.target.checked)}
                                                    />
                                                    <span>Select All</span>
                                                </label>
                                            </th>
                                            <th>Name</th>
                                            <th>Role</th>
                                            <th>Phone Number</th>
                                            <th>Email</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayStaffList.map((staff, i) => {
                                            const roleId = typeof staff.role_id === "number" ? staff.role_id : undefined
                                            const checked = roleId != null && selectedRoleIds.includes(roleId)
                                            const roleLabel = (staff?.role_name ? COMPANY_ROLE_NAMES[staff.role_name] : undefined) ?? staff?.role ?? staff?.role_name ?? ""
                                            const edits =
                                                roleId != null
                                                    ? transferStaffEdits[roleId] || {
                                                          mobile_no: String(staff.mobile_no ?? ""),
                                                          email: String(staff.email ?? ""),
                                                      }
                                                    : { mobile_no: "", email: "" }
                                            return (
                                                <tr key={staff.role_id ?? i} className={transferStyles.tableRow}>
                                                    <td>
                                                        {roleId != null && (
                                                            <input
                                                                data-testid={`company-role-transfer-row-${roleId}-checkbox`}
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={(e) => toggleRole(roleId, e.target.checked)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td className={transferStyles.nameCell}>{staff.name ?? "—"}</td>
                                                    <td>{roleLabel}</td>
                                                    <td>
                                                        {checked && roleId != null ? (
                                                            <div>
                                                                <input
                                                                    type="text"
                                                                    data-testid={`company-role-transfer-row-${roleId}-phone-input`}
                                                                    value={edits.mobile_no}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value
                                                                        setTransferStaffEdits((prev) => ({
                                                                            ...prev,
                                                                            [roleId]: { ...edits, mobile_no: v },
                                                                        }))
                                                                    }}
                                                                    className={cn(
                                                                        "w-full rounded border px-2 py-1 text-sm",
                                                                        validationErrors[roleId]?.mobile_no
                                                                            ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20"
                                                                            : "border-slate-200"
                                                                    )}
                                                                    placeholder="Phone"
                                                                />
                                                                {validationErrors[roleId]?.mobile_no && (
                                                                    <div className={companyFormStyles.errorMessage} style={{ fontSize: "11px", marginTop: "4px" }} role="alert">
                                                                        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                                                        {validationErrors[roleId].mobile_no}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            formatPhone(staff.mobile_no)
                                                        )}
                                                    </td>
                                                    <td>
                                                        {checked && roleId != null ? (
                                                            <div>
                                                                <input
                                                                    type="text"
                                                                    data-testid={`company-role-transfer-row-${roleId}-email-input`}
                                                                    value={edits.email}
                                                                    onChange={(e) => {
                                                                        setTransferStaffEdits((prev) => ({
                                                                            ...prev,
                                                                            [roleId]: { ...edits, email: e.target.value },
                                                                        }))
                                                                    }}
                                                                    className={cn(
                                                                        "w-full rounded border px-2 py-1 text-sm",
                                                                        validationErrors[roleId]?.email
                                                                            ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500/20"
                                                                            : "border-slate-200"
                                                                    )}
                                                                    placeholder="Email"
                                                                />
                                                                {validationErrors[roleId]?.email && (
                                                                    <div className={companyFormStyles.errorMessage} style={{ fontSize: "11px", marginTop: "4px" }} role="alert">
                                                                        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                                                        {validationErrors[roleId].email}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            staff.email ?? "—"
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className={`${transferStyles.footer} ${modalStyles.modalFooterActionGroup}`}>
                        <button
                            data-testid="company-role-transfer-submit-button"
                            type="submit"
                            disabled={!canSubmit}
                            className={modalStyles.modalActionPrimary}
                        >
                            {submitting && <Loader2 size={16} className="animate-spin" />}
                            Transfer
                        </button>
                        <button
                            data-testid="company-role-transfer-cancel-button"
                            type="button"
                            onClick={onClose}
                            className={modalStyles.modalActionSecondary}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )

    return createPortal(content, document.body)
}
